import React, {useState, useEffect} from 'react';
import {useObserver} from 'mobx-react-lite';
import {Button, Card, Tag, Icon, Intent, Text, Divider, Tooltip, HTMLTable} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {AppDetailStore} from '../../../stores/AppDetailStore';
import {ZipApi, OpalTool, OpalInstance} from '../../../lib/ZipApi';
import {directoryStore} from '../../../stores/DirectoryStore';
import styles from './Opal.module.sass';
import { showToasts } from '../../../lib/toaster';
import { OpalToolRow } from './OpalToolRow';
import { handleExpiredOptiAccessToken, SESSION_EXPIRED_MESSAGE } from './opalUtils';
import { getApiErrorMessage } from '../../../lib/ZipApiError';

interface Props {
  detailStore: AppDetailStore;
}

interface OpalToolWithInstance extends OpalTool {
  instance: OpalInstance;
}

const NO_OPAL_ACCESS_MESSAGE = 'You do not have admin access to a Mark account. Contact your administrator to register tools in Mark.';

export const Opal = ({detailStore}: Props) => {
  const [instances, setInstances] = useState<OpalInstance[]>([]);
  const [opalToolsWithInstances, setOpalToolsWithInstances] = useState<OpalToolWithInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [instancesLoading, setInstancesLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const fetchAllOpalTools = async () => {
    if (!directoryStore.trackerId || !detailStore.installId || instances.length === 0) {
      return;
    }

    try {
      const optiidToken = directoryStore.getOptiAccessToken();
      const allToolsWithInstances: OpalToolWithInstance[] = [];
      await Promise.all(
        instances.map(async (instance) => {
          try {
            const response = await ZipApi.listOpalTools(
              directoryStore.trackerId!,
              instance.InstanceId,
              detailStore.installId!,
              instance.Region,
              optiidToken
            );
            const tools = response.tools || [];
            const toolsWithInstance = tools.map((tool) => ({
              ...tool,
              instance
            }));

            allToolsWithInstances.push(...toolsWithInstance);
          } catch (error) {
            if (!handleExpiredOptiAccessToken(error)) {
              console.error('Error fetching Opal tools:', error);
              const errorMessage = await getApiErrorMessage(error, 'Error fetching Mark accounts');
              showToasts([{intent: Intent.DANGER, message: errorMessage}]);
            }
            setOpalToolsWithInstances([]);
          }
        })
      );

      const sortedTools = allToolsWithInstances.sort((a, b) => {
        const instanceCompare = a.instance.InstanceName.localeCompare(b.instance.InstanceName);
        if (instanceCompare !== 0) return instanceCompare;
        return a.name.localeCompare(b.name);
      });

      setOpalToolsWithInstances(sortedTools);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error, 'Error fetching Mark tools');
      showToasts([{intent: Intent.DANGER, message: errorMessage}]);
      setOpalToolsWithInstances([]);
    }
  };

  useEffect(() => {
    const fetchOpalInstances = async () => {
      if (directoryStore.trackerId) {
        setInstancesLoading(true);
        try {
          const organizationId = directoryStore.getOrganizationId();
          const userEmail = directoryStore.getUserEmail();
          const opalInstances = await ZipApi.listOpalInstances(organizationId, userEmail);
          setInstances(opalInstances);
        } catch (error) {
          if (handleExpiredOptiAccessToken(error)) {
            setSessionExpired(true);
          } else {
            console.error('Error fetching Opal instances:', error);
            const errorMessage = await getApiErrorMessage(error, 'Error fetching Mark accounts');
            showToasts([{intent: Intent.DANGER, message: errorMessage}]);
          }
          setInstances([]);
        } finally {
          setInstancesLoading(false);
        }
      }
    };

    fetchOpalInstances();
  }, [directoryStore.trackerId]);

  useEffect(() => {
    const fetchOpalTools = async () => {
      if (instances.length > 0 && directoryStore.trackerId && detailStore.installId) {
        setLoading(true);
        try {
          await fetchAllOpalTools();
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOpalTools();
  }, [instances, directoryStore.trackerId, detailStore.installId]);

  const handleToolUpdated = () => {
    // Refresh the tools list
    fetchAllOpalTools();
  };

  return useObserver(() => {
    return (
      <div>
        {instances.length > 0 && (
          <div className={styles.header}>
            <Text className={styles.description}>
              Register tools in Mark. Make sure the app is authorized and configured in the Settings tab.
            </Text>
          </div>
        )}

      <div className={styles.tableContainer}>
        {(loading || instancesLoading) ? (
          <div className={styles.loadingMessage}>
            <Text>Loading Mark tools...</Text>
          </div>
        ) : instances.length === 0 ? (
          <div className={styles.emptyMessage}>
            <Text>{sessionExpired ? SESSION_EXPIRED_MESSAGE : NO_OPAL_ACCESS_MESSAGE}</Text>
          </div>
        ) : opalToolsWithInstances.length === 0 ? (
          <div className={styles.emptyMessage}>
            <Text>No tools available.</Text>
          </div>
        ) : (
          <HTMLTable striped className={styles.table}>
            <thead>
              <tr>
                <th>Mark Account</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {opalToolsWithInstances.map((toolWithInstance) => (
                <OpalToolRow
                  key={`${toolWithInstance.instance.InstanceId}-${toolWithInstance.name}`}
                  tool={toolWithInstance}
                  instance={toolWithInstance.instance}
                  detailStore={detailStore}
                  onToolUpdated={handleToolUpdated}
                />
              ))}
            </tbody>
          </HTMLTable>
        )}
      </div>
    </div>
    );
  });
};
